import type { Quality } from '@/pixi/quality';

/** What the Settings sheet can hold: a pinned tier, or `auto` (the governor decides). */
export type TierChoice = 'auto' | Quality;

/** The tier on a screen this dense or denser, when the player has not pinned one. */
export const PHONE_DPR = 2.75;

const ORDER: readonly Quality[] = ['low', 'medium', 'high'];
const lower = (a: Quality, b: Quality): Quality => (ORDER.indexOf(a) <= ORDER.indexOf(b) ? a : b);

/**
 * The quality tier a chest reveal draws at.
 *
 * A tier pinned in Settings is the player's word and stands. On `auto` the chest used to draw at `high` whatever the
 * device was (it read the pin and fell back to `high`, never the governor's live tier), and `high` is a 3x canvas under
 * a stack of full-screen light layers: on a 1080-wide phone (3x at 360 css px, the common mid-range Android) a legendary
 * open dropped about twice the 60 Hz frames of the live build in the round-6 A/B. So on `auto` it follows the governor
 * (capped by the device's recommendation: a touch screen opens at `medium`), and a touch-primary screen of `PHONE_DPR` or more is held
 * at `cap` (the lighter DOM and the smaller canvas) unless the player pins `high`.
 */
export function chestTier(
  choice: TierChoice,
  live: Quality,
  screen: { readonly dpr: number; readonly recommended: Quality; readonly coarse: boolean },
  cap: Quality = 'low',
): Quality {
  if (choice !== 'auto') return choice;
  // The governor's live tier is only the module default (`high`) until a game has run on this page, so on a phone that
  // opens a chest from the home screen it said `high` (the round-6c probe: a coarse-pointer 390x844 at 2x landed on
  // high). The device's own recommendation is a ceiling too: a touch screen opens at medium and stays there, however
  // well the governor thinks it is doing. After a game the governor's demotions count (min), its promotions stop there.
  const tier = lower(live, screen.recommended);
  // A dense screen is a phone only if its PRIMARY pointer is coarse: a hybrid laptop is touch-capable with a fine primary
  // pointer and is not held back (`pointer: coarse` is the primary pointer, `any-pointer` is what the hybrid also has).
  return screen.coarse && screen.dpr >= PHONE_DPR ? lower(tier, cap) : tier;
}
